import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Tabs, Empty, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import CouponAndVoucherCell from './subviews/CouponAndVoucherCell';
import { fetchLoyaltyItems } from 'services/walletService';
import './CouponsAndVouchers.scss';
import couponRoundIcon from 'assets/images/coupon_round.png';
import { BackendLoyaltyItem } from 'types/dto/wallet';

interface CouponsAndVouchersProps {
  isExpiredOrUsed?: boolean;
}

const CouponsAndVouchers: React.FC<CouponsAndVouchersProps> = ({isExpiredOrUsed = false,}) => {
  const { t, i18n } = useTranslation('wallet');
  const { t: tCommon } = useTranslation('common');

  const [coupons, setCoupons] = useState<BackendLoyaltyItem[]>([]);
  const [vouchers, setVouchers] = useState<BackendLoyaltyItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef<IntersectionObserver | null>(null);
  const PAGE_SIZE = 10;
  const [couponsTotal, setCouponsTotal] = useState(0);
  const [vouchersTotal, setVouchersTotal] = useState(0);

  const lastItemRef = useCallback((node: HTMLDivElement | null) => {
    if (isLoading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        setPage((prevPage) => prevPage + 1);
      }
    });

    if (node) observer.current.observe(node);
  }, [isLoading, hasMore]);

  useEffect(() => {
    setCoupons([]);
    setVouchers([]);
    setCouponsTotal(0);
    setVouchersTotal(0);
    setPage(1);
    setHasMore(true);
    setLoadError(false);
  }, [isExpiredOrUsed]);

  useEffect(() => {
    const loadItems = async () => {
      try {
        setIsLoading(true);
        setLoadError(false);

        if (isExpiredOrUsed) {

          const [expVocRes, expProRes, usedVocRes, usedProRes] = await Promise.all([
            fetchLoyaltyItems('VOC-', 'expired', page),
            fetchLoyaltyItems('PRO-', 'expired', page),
            fetchLoyaltyItems('VOC-', 'used', page),
            fetchLoyaltyItems('PRO-', 'used', page)
          ]);

          const totalC = (expProRes?.data?.total || 0) + (usedProRes?.data?.total || 0);
          const totalV = (expVocRes?.data?.total || 0) + (usedVocRes?.data?.total || 0);

          setCouponsTotal(totalC);
          setVouchersTotal(totalV);

          const sortUsedFirst = (a: BackendLoyaltyItem, b: BackendLoyaltyItem) => {
            const aUsed = a.usage?.used === 1 ? 1 : 0;
            const bUsed = b.usage?.used === 1 ? 1 : 0;
            return bUsed - aUsed;
          };

          const combinedPro = [
            ...(expProRes?.data?.results || []),
            ...(usedProRes?.data?.results || [])
          ];
          const uniqueCoupons = Array.from(new Map(combinedPro.map(item => [item.id, item])).values()).sort(sortUsedFirst);

          const combinedVoc = [
            ...(expVocRes?.data?.results || []),
            ...(usedVocRes?.data?.results || [])
          ];
          const uniqueVouchers = Array.from(new Map(combinedVoc.map(item => [item.id, item])).values()).sort(sortUsedFirst);



          setCoupons(prev => (page === 1 ? uniqueCoupons : [...prev, ...uniqueCoupons]));
          setVouchers(prev => (page === 1 ? uniqueVouchers : [...prev, ...uniqueVouchers]));

          const hasAnyMore = [
              expVocRes?.data?.results?.length || 0,
              expProRes?.data?.results?.length || 0,
              usedVocRes?.data?.results?.length || 0,
              usedProRes?.data?.results?.length || 0
          ].some(len => len === PAGE_SIZE);

          setHasMore(hasAnyMore);

        } else {
          const [couponsRes, vouchersRes] = await Promise.all([
            fetchLoyaltyItems('PRO-', '', page),
            fetchLoyaltyItems('VOC-', '', page)
          ]);

          const cTotal = couponsRes?.data?.total || 0;
          const vTotal = vouchersRes?.data?.total || 0;

          setCouponsTotal(cTotal);
          setVouchersTotal(vTotal);


          const newCoupons = couponsRes?.data?.results || [];
          const newVouchers = vouchersRes?.data?.results || [];

          setCoupons(prev => (page === 1 ? newCoupons : [...prev, ...newCoupons]));
          setVouchers(prev => (page === 1 ? newVouchers : [...prev, ...newVouchers]));

          const couponsTotal = couponsRes?.data?.total || 0;
          const vouchersTotal = vouchersRes?.data?.total || 0;
          setHasMore(page * 10 < couponsTotal || page * 10 < vouchersTotal);
        }
      } catch (err) {
        setLoadError(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadItems();
  }, [i18n.language, isExpiredOrUsed, page]);

  const renderCouponList = (
    items: BackendLoyaltyItem[],
    type: 'coupons' | 'vouchers'
  ) => {
    if (items.length === 0 && !isLoading) {
      return (
        <Empty
          image={<img src={couponRoundIcon} className="empty-state-image" />}
          description={
            type === 'coupons'
              ? t('noCouponsAvailable')
              : t('noVouchersAvailable')
          }
        />
      );
    }
    const canShowSentry = items.length >= PAGE_SIZE && hasMore;

    return (
      <div className="coupon-list-wrapper">
        <div className="coupon-grid">
          {items.map((coupon: BackendLoyaltyItem) => (
            <CouponAndVoucherCell
              key={coupon.id}
              coupon={coupon}
              needUseButton={!isExpiredOrUsed}
              isExpiredOrUsed={isExpiredOrUsed}
            />
          ))}
        </div>

        {canShowSentry && (
          <div ref={lastItemRef} className="scroll-sentry">
            {isLoading && <Skeleton active paragraph={{ rows: 1 }} />}
          </div>
        )}
      </div>
    );
  };

  const tabItems = [
    {
      key: 'coupons',
      label: `${t('coupons', 'Coupons')} (${isLoading && page === 1 ? '...' : couponsTotal})`,
      children: renderCouponList(coupons, 'coupons'),
    },
    {
      key: 'vouchers',
      label: `${t('vouchers', 'Vouchers')} (${isLoading && page === 1 ? '...' : vouchersTotal})`,
      children: renderCouponList(vouchers, 'vouchers'),
    },
  ];

  return (
    <div className="coupons-and-vouchers-container">
      <div className="coupons-and-vouchers-content">
        <div className="coupons-section">
          <div className="vls-section-title">
            {isExpiredOrUsed
              ? t('expiredAndUsed', 'Expired & Used')
              : t('couponsAndVouchers', 'Coupons & Vouchers')}
          </div>

          {/* Show loading skeleton overlay while data is loading */}
          {isLoading && page === 1 && (
            <div className="skeleton-card">
              <Skeleton active paragraph={{ rows: 4 }} />
            </div>
          )}

          {/* Show error alert if loading failed */}
          {!isLoading && loadError && (
            <div className="load-data-error">
              {tCommon('error.loadDataError')}
            </div>
          )}

          {(page > 1 || (!isLoading && !loadError)) && (
            <Tabs
              defaultActiveKey="coupons"
              items={tabItems}
              className="coupons-tabs"
              size="large"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CouponsAndVouchers;